import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function FrogCard({ frogs }) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!frogs || frogs.length === 0) return null;

  const activeFrog = frogs[activeIndex];
  const title = "Chokoladefrøer";
  const variantTitle = activeFrog.title?.da || activeFrog.title || 'Uden titel';
  const descriptionHtml = activeFrog.description?.da || activeFrog.description || '';
  const price = 14; // Alle frøer koster 14 kr. jf. prislisten

  const imageUrl = activeFrog.images?.[0] || 'https://assets.auxodev.dk/assets/2c62fa2f-a0e0-45c6-84dc-1b9d3147e5ca/chokolenten-hero.jpg';

  // Optimer det aktive billede med Cloudflare on-the-fly resizing (600px bredde)
  const optimizedImageUrl = imageUrl.startsWith('https://assets.auxodev.dk')
    ? imageUrl.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/')
    : imageUrl;

  return (
    <div className="group bg-cream-light rounded-2xl border border-choc-dark/10 overflow-hidden shadow-sm transition-all duration-300 hover:shadow-md hover:border-berry-accent/30 flex flex-col h-full md:col-span-2 lg:col-span-2">
      
      {/* To-kolonne layout på større skærme for at give plads til galleriet */}
      <div className="grid grid-cols-1 sm:grid-cols-2 h-full">
        
        {/* Venstre side: Billede & Pris */}
        <div className="relative aspect-square sm:aspect-auto sm:h-full overflow-hidden bg-choc-dark/5 min-h-[300px]">
          <img
            src={optimizedImageUrl}
            alt={variantTitle}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102"
            loading="lazy"
          />
          
          {/* Variant label i bunden af billedet */}
          <div className="absolute bottom-4 left-4 right-4 bg-choc-dark/80 backdrop-blur-xs text-cream-light px-3 py-2 rounded-xl text-xs font-medium font-sans flex items-center justify-between">
            <span>Aktiv variant:</span>
            <span className="font-bold text-berry-light">{variantTitle}</span>
          </div>
        </div>

        {/* Højre side: Beskrivelse & Vælgere */}
        <div className="p-6 flex flex-col justify-between h-full bg-cream-light/40">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-serif font-bold text-choc-dark group-hover:text-berry-accent transition-colors">
                {title}
              </h3>
              <span className="text-xs font-sans font-bold uppercase tracking-wider text-berry-accent bg-berry-accent/10 px-2.5 py-1 rounded-full">
                Vores favoritter
              </span>
            </div>

            {/* Variantvælger (Thumbnails) */}
            <div className="mb-6">
              <p className="text-xs font-sans font-semibold text-choc-dark/50 uppercase tracking-wider mb-2">
                Klik for at se varianter ({frogs.length}):
              </p>
              <div className="flex flex-wrap gap-2">
                {frogs.map((frog, index) => {
                  const imgUrl = frog.images?.[0] || '';
                  const optimizedThumbUrl = imgUrl.startsWith('https://assets.auxodev.dk')
                    ? imgUrl.replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=100,height=100,fit=cover,quality=80,format=auto/assets/')
                    : imgUrl;
                  const isSelected = index === activeIndex;
                  const fTitle = frog.title?.da || frog.title || '';
                  
                  return (
                    <button
                      key={frog.id}
                      onClick={() => setActiveIndex(index)}
                      className={`w-11 h-11 rounded-xl overflow-hidden border-2 transition-all cursor-pointer relative ${
                        isSelected 
                          ? 'border-berry-accent scale-105 shadow-sm ring-2 ring-berry-accent/20' 
                          : 'border-choc-dark/10 hover:border-choc-dark/30'
                      }`}
                      title={fTitle}
                    >
                      <img 
                        src={optimizedThumbUrl} 
                        alt={fTitle} 
                        className="w-full h-full object-cover" 
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-berry-accent/10 flex items-center justify-center">
                          <div className="w-2 h-2 bg-berry-accent rounded-full" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Variant beskrivelse */}
            <div className="border-t border-choc-dark/5 pt-4 mb-6">
              <h4 className="text-sm font-sans font-bold text-choc-dark mb-1">
                Om {variantTitle}:
              </h4>
              {descriptionHtml ? (
                <div 
                  className="text-sm text-choc-dark/70 leading-relaxed font-sans prose prose-sm prose-chocolate"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              ) : (
                <p className="text-sm text-choc-dark/50 italic">
                  Ingen beskrivelse tilgængelig for denne variant.
                </p>
              )}
            </div>
          </div>

          {/* CTA Knapper */}
          <div className="flex flex-col gap-2 mt-auto border-t border-choc-dark/5 pt-4">
            <Link
              to="/kontakt"
              state={{ productTitle: `${title} (${variantTitle})` }}
              className="w-full text-center bg-berry-accent hover:bg-berry-accent/90 text-white font-sans text-sm font-semibold py-3 px-4 rounded-xl transition-colors shadow-sm hover:shadow cursor-pointer"
            >
              Kontakt os vedr. chokolade
            </Link>
            <a
              href="https://www.instagram.com/chokolenten/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full text-center bg-transparent hover:bg-choc-dark/5 text-choc-dark border border-choc-dark/20 font-sans text-xs font-medium py-2 px-4 rounded-xl transition-colors cursor-pointer"
            >
              <i className="fa-brands fa-instagram mr-1.5" />
              Se flere frøer på Instagram
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
